import { ref, Ref } from "vue"

export const useDragToScroll = (container: Ref<HTMLElement>) => {
  const startedDragging = ref(false)
  const startX = ref(0)
  const scrollLeftForDrag = ref(0)
  const dragEnabled = ref(false)

  const {
    deactivateChildrenTextSelection,
    activateChildrenTextSelection,
    deactivateChildrenMouseEvents,
    activateChildrenMouseEvents,
  } = useClickAndTextSelectionPrevention(container)

  let thresholdTimer

  const startDragging = () => {
    deactivateChildrenMouseEvents()
    startedDragging.value = true
    scrollLeftForDrag.value = container.value.scrollLeft
    container.value.style.cursor = "grabbing"
  }

  const handleMouseDown = (e: MouseEvent) => {
    startX.value = e.pageX
    deactivateChildrenTextSelection()
    // Start the drag after a threshold time to still allow click events to be triggered
    thresholdTimer = setTimeout(() => {
      startDragging()
    }, 300)
  }

  const stopDragging = () => {
    clearTimeout(thresholdTimer)
    thresholdTimer = null
    activateChildrenTextSelection()
    activateChildrenMouseEvents()
    startedDragging.value = false
    container.value.style.cursor = "grab"
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!startedDragging.value) {
      // if the user moves the mouse before the threshold time, just start dragging
      if (thresholdTimer) {
        clearTimeout(thresholdTimer)
        thresholdTimer = null
        startDragging()
      }
      return
    }
    e.preventDefault()
    const pageX = e.pageX
    const walk = pageX - startX.value
    container.value.scrollLeft = scrollLeftForDrag.value - walk
  }

  const enableDragToScroll = () => {
    if (dragEnabled.value) return
    dragEnabled.value = true
    container.value.style.cursor = "grab"
    container.value.addEventListener("mousedown", handleMouseDown)
    container.value.addEventListener("mouseleave", stopDragging)
    container.value.addEventListener("mouseup", stopDragging)
    container.value.addEventListener("mousemove", handleMouseMove)
  }

  const disableDragToScroll = () => {
    dragEnabled.value = false
    container.value.style.cursor = "unset"
    container.value.removeEventListener("mousedown", handleMouseDown)
    container.value.removeEventListener("mouseleave", stopDragging)
    container.value.removeEventListener("mouseup", stopDragging)
    container.value.removeEventListener("mousemove", handleMouseMove)
  }

  const enableOnDevicesGreaterThanPhones = () => {
    if (window.innerWidth <= 600) {
      disableDragToScroll()
    } else {
      enableDragToScroll()
    }
  }

  const registerDragToScroll = () => {
    enableOnDevicesGreaterThanPhones()
    window.addEventListener("resize", enableOnDevicesGreaterThanPhones)
  }

  const unregisterDragToScroll = () => {
    window.removeEventListener("resize", enableOnDevicesGreaterThanPhones)
    disableDragToScroll()
  }

  return {
    registerDragToScroll,
    unregisterDragToScroll,
  }
}

const useClickAndTextSelectionPrevention = (container: Ref<HTMLElement>) => {
  // Adds a style tag with CSS to the head of the document for
  // deactivating pointer events & text-selection on children
  const registerNoPointerStyleClass = () => {
    const style = document.createElement("style")
    style.innerHTML = `
      .drag-to-scroll-no-pointer {
        pointer-events: none;
      }
      .drag-to-scroll-prevent-text-selection {
        -webkit-user-select: none; /* Safari */
        -ms-user-select: none; /* IE 10 and IE 11 */
        user-select: none; /* Standard syntax */
      }
    `
    document.head.appendChild(style)
  }

  registerNoPointerStyleClass()

  const deactivateChildrenTextSelection = () => {
    container.value.querySelectorAll("*").forEach((el) => {
      if (el instanceof HTMLElement) {
        el.classList.add("drag-to-scroll-prevent-text-selection")
      }
    })
  }

  const activateChildrenTextSelection = () => {
    container.value.querySelectorAll("*").forEach((el) => {
      if (el instanceof HTMLElement) {
        el.classList.remove("drag-to-scroll-prevent-text-selection")
      }
    })
  }

  const deactivateChildrenMouseEvents = () => {
    container.value.querySelectorAll("*").forEach((el) => {
      if (el instanceof HTMLElement) {
        el.classList.add("drag-to-scroll-no-pointer")
      }
    })
  }

  const activateChildrenMouseEvents = () => {
    container.value.querySelectorAll("*").forEach((el) => {
      if (el instanceof HTMLElement) {
        el.classList.remove("drag-to-scroll-no-pointer")
      }
    })
  }

  return {
    deactivateChildrenTextSelection,
    activateChildrenTextSelection,
    deactivateChildrenMouseEvents,
    activateChildrenMouseEvents,
  }
}
